<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">权益券</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 种券</span>
        <span class="divider">|</span>
        <span>上架 <b>{{ statusCounts['1'] || 0 }}</b></span>
        <span class="divider">|</span>
        <span>下架 <b>{{ statusCounts['0'] || 0 }}</b></span>
        <span class="divider">|</span>
        <span>累计领取 <b>{{ totalReceived }}</b> 张</span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="券名称">
            <el-input
              v-model="query.keyword"
              placeholder="券名称"
              clearable
              style="width: 180px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="券类型">
            <el-select v-model="query.category" placeholder="全部" clearable style="width: 120px">
              <el-option v-for="c in BENEFIT_CATEGORY" :key="c.value" :label="c.label" :value="c.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="发放方式">
            <el-select v-model="query.dispatch" placeholder="全部" clearable style="width: 130px">
              <el-option v-for="d in DISPATCH_FILTER" :key="d.value" :label="d.label" :value="d.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 110px">
              <el-option v-for="s in BENEFIT_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleCreate">新增权益券</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <!-- 券 -->
        <el-table-column label="权益券" min-width="300">
          <template slot-scope="scope">
            <div class="benefit-cell">
              <el-image :src="scope.row.cover" :preview-src-list="[scope.row.cover]" fit="cover" class="benefit-cover" />
              <div class="benefit-main">
                <div class="benefit-name">
                  {{ scope.row.name }}
                  <el-tag :type="benefitCategoryTagType(scope.row.category)" size="mini" effect="plain">
                    {{ benefitCategoryText(scope.row.category) }}
                  </el-tag>
                </div>
                <div class="benefit-desc">{{ scope.row.description || '暂无简介' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <!-- 有效期 -->
        <el-table-column label="有效期" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            <span :class="{ 'cell-muted': !scope.row.start_date && scope.row.valid_type === 1 }">
              {{ validText(scope.row) }}
            </span>
          </template>
        </el-table-column>
        <!-- 发放量 -->
        <el-table-column label="发放量" min-width="170">
          <template slot-scope="scope">
            <div class="stock-cell">
              <span class="stock-text">{{ stockText(scope.row) }}</span>
              <el-progress
                v-if="scope.row.total"
                :percentage="stockPercent(scope.row)"
                :show-text="false"
                :stroke-width="4"
                color="#8E1103"
              />
            </div>
          </template>
        </el-table-column>
        <!-- 状态 -->
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="benefitStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ benefitStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 创建时间 -->
        <el-table-column label="创建时间" width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.created_at }}</template>
        </el-table-column>
        <!-- 操作 -->
        <el-table-column label="操作" width="220" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.status === 1"
              type="text" size="mini" class="text-primary-btn" @click="handleGrant(scope.row)"
            >发放</el-button>
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button
              v-if="scope.row.status === 1"
              type="text" size="mini" class="text-warning-btn"
              @click="handleStatus(scope.row, 0)"
            >停用</el-button>
            <el-button v-else type="text" size="mini" @click="handleStatus(scope.row, 1)">启用</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 新增 / 编辑弹框 -->
    <el-dialog
      :title="form.id ? '编辑权益券' : '新增权益券'"
      :visible.sync="formVisible"
      width="760px"
      top="5vh"
      class="benefit-form-dialog"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="110px" size="small">
        <div class="form-section">
          <div class="form-section-title">券基础信息</div>
          <el-form-item label="券名称" prop="name">
            <el-input v-model="form.name" maxlength="60" show-word-limit placeholder="如：桂林3日旅居权益券" />
          </el-form-item>
          <el-form-item label="券类型" prop="category">
            <el-select v-model="form.category" placeholder="请选择券类型" style="width: 220px">
              <el-option v-for="c in BENEFIT_CATEGORY" :key="c.value" :label="c.label" :value="c.value" />
            </el-select>
            <span class="form-tip">决定券在小程序端归属的类目</span>
          </el-form-item>
          <el-form-item label="券封面" prop="cover">
            <image-uploader
              v-model="form.cover"
              tip="上传封面"
              hint="横版封面，建议 750×420，JPG / PNG"
              @input="onCoverChange"
            />
          </el-form-item>
          <el-form-item label="券简介" prop="description">
            <el-input
              v-model="form.description"
              type="textarea"
              :rows="2"
              maxlength="100"
              show-word-limit
              placeholder="一句话说明券的用途，展示在小程序券列表"
            />
          </el-form-item>
          <el-form-item label="使用规则" prop="rules">
            <el-input
              v-model="form.rules"
              type="textarea"
              :rows="4"
              maxlength="500"
              show-word-limit
              placeholder="每行一条，如：每张券限用一次，不可叠加使用"
            />
            <span class="form-tip">每行一条规则，小程序端按行展示</span>
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">有效期</div>
          <el-form-item label="有效期" prop="valid_type">
            <el-radio-group v-model="form.valid_type">
              <el-radio :label="1">固定日期</el-radio>
              <el-radio :label="2">发放后按天</el-radio>
              <el-radio :label="3">长期有效</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="form.valid_type === 1" label="有效日期" prop="dateRange">
            <el-date-picker
              v-model="form.dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              style="width: 320px"
            />
            <span class="form-tip">到期后未使用的券自动失效</span>
          </el-form-item>
          <el-form-item v-else-if="form.valid_type === 2" label="有效天数" prop="valid_days">
            <el-input-number v-model="form.valid_days" :min="1" :max="365" controls-position="right" style="width: 160px" />
            <span class="form-tip">自发放到用户账户之日起计算</span>
          </el-form-item>
          <el-form-item v-else label="有效期限">
            <span class="form-tip">长期有效：发放到用户账户后永久可用，不设到期时间，无需填写日期与天数</span>
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">发放设置</div>
          <el-form-item label="发放总量" prop="total">
            <el-input-number v-model="form.total" :min="0" :max="999999" controls-position="right" style="width: 160px" />
            <span class="form-tip">填 0 表示不限量；保存后在列表点「发放」选择用户，余量不足时拦截</span>
          </el-form-item>
          <el-form-item label="自动发放">
            <el-checkbox-group v-model="form.dispatchIdList" class="dispatch-checks">
              <el-checkbox v-for="d in DISPATCH_OPTIONS" :key="d.value" :label="d.value">{{ d.label }}</el-checkbox>
            </el-checkbox-group>
            <span class="form-tip">选择参与对象，可同时勾选：勾「每月发放」全体用户参与；勾「里程碑发放」达成条件的用户参与。都不勾选即仅手动发放</span>
          </el-form-item>
          <el-form-item v-if="hasMonthly || hasMilestone" label="发放张数" required>
            <el-input-number
              v-model="form.milestone_num"
              :min="1" :max="10" controls-position="right"
              style="width: 140px"
            />
            <span class="cycle-unit">张</span>
            <span class="form-tip">每次自动发放的张数，每月发放与里程碑发放共用</span>
          </el-form-item>
          <el-form-item v-if="hasMonthly && !(hasMilestone && Number(form.milestone_mode) === 2)" label="每月发放日" required>
            <el-select v-model="form.dispatch_day" placeholder="选择几号发放" style="width: 140px">
              <el-option v-for="d in DISPATCH_DAYS" :key="d.value" :label="d.label" :value="d.value" />
            </el-select>
            <span class="form-tip">每月这天给全体用户自动发放，不受发放总量限制；{{ nextDispatchText(form.dispatch_day, 1) }}发放</span>
          </el-form-item>
          <el-form-item v-if="hasMilestone" label="达成条件" required>
            <el-select v-model="form.milestone_id" placeholder="选择触发条件" style="width: 220px">
              <el-option v-for="m in milestoneOptions" :key="m._id" :label="m.label" :value="m._id" />
            </el-select>
            <span class="form-tip">{{ hasMonthly ? '达成条件的用户按下方方式参与自动发放' : '用户达成后自动发放一次，发完即止' }}</span>
          </el-form-item>
          <el-form-item v-if="hasMonthly && hasMilestone" label="达成用户发放" required>
            <el-radio-group v-model="dispatchMode">
              <el-radio :label="1">跟随每月发放日</el-radio>
              <el-radio :label="2">周期发放</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="hasMonthly && hasMilestone && Number(form.milestone_mode) === 2" label="间隔天数" required>
            <el-input-number
              v-model="form.milestone_interval_days"
              :min="1" :max="365" controls-position="right"
              style="width: 140px"
            />
            <span class="cycle-unit">天</span>
            <span class="form-tip">达成用户从达成日起每隔这么多天发一次，循环发放，如每 30 天发 1 张</span>
          </el-form-item>
          <el-form-item label="启用状态">
            <el-switch v-model="form.status" :active-value="1" :inactive-value="0" />
            <span class="form-tip">{{ form.status === 1 ? '启用后可手动发放，自动规则同步生效' : '停用后暂停发放，已发放的券不受影响' }}</span>
          </el-form-item>
        </div>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="formVisible = false">取 消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 发放弹框 -->
    <benefit-grant-dialog
      :visible.sync="grantVisible"
      :benefit="grantRow"
      @granted="loadData"
    />
  </div>
</template>

<script>
import {
  BENEFIT_CATEGORY, BENEFIT_STATUS, emptyBenefit,
  DISPATCH_DAYS, DISPATCH_OPTIONS, DISPATCH_FILTER,
  benefitCategoryText, benefitCategoryTagType, benefitStatusText, benefitStatusTagType,
  splitDispatchValues, nextDispatchText,
  validText, stockText, stockPercent, canGrant
} from '@/mock/benefit'
import {
  getBenefitList, createBenefit, updateBenefit, deleteBenefit, setBenefitStatus
} from '@/api/benefit'
import dictOptions from '@/mixins/dict-options'
import ImageUploader from '@/components/ImageUploader.vue'
import BenefitGrantDialog from '@/components/BenefitGrantDialog.vue'

export default {
  name: 'BenefitListManage',
  components: { ImageUploader, BenefitGrantDialog },
  mixins: [dictOptions],
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      statusCounts: {},
      totalReceived: 0,
      query: { keyword: '', category: '', dispatch: '', status: '' },
      page: 1,
      pageSize: 10,
      formVisible: false,
      form: emptyBenefit(),
      saving: false,
      grantVisible: false,
      grantRow: null,
      // 里程碑达成条件（字典 dispatch_milestone）：运营可增删，故选项从 mg_dict 拉；发放方式是固定两种，见 DISPATCH_OPTIONS
      milestoneOptions: [],
      BENEFIT_CATEGORY,
      BENEFIT_STATUS,
      DISPATCH_DAYS,
      DISPATCH_OPTIONS,
      DISPATCH_FILTER,
      benefitCategoryText,
      benefitCategoryTagType,
      benefitStatusText,
      benefitStatusTagType,
      splitDispatchValues,
      nextDispatchText,
      canGrant,
      validText,
      stockText,
      stockPercent,
      rules: {
        name: [{ required: true, message: '请输入券名称', trigger: 'blur' }],
        category: [{ required: true, message: '请选择券类型', trigger: 'change' }],
        cover: [{ required: true, message: '请上传券封面', trigger: 'change' }],
        description: [{ required: true, message: '请填写券简介', trigger: 'blur' }],
        dateRange: [{
          // 只有「固定日期」才要求日期区间；「发放后按天」「长期有效」下该字段隐藏且不校验
          validator: (rule, value, callback) => {
            if (Number(this.form.valid_type) !== 1) return callback()
            if (!Array.isArray(value) || value.length < 2 || !value[0] || !value[1]) {
              return callback(new Error('请选择有效日期'))
            }
            callback()
          },
          trigger: 'change'
        }],
        valid_days: [{
          // 只有「发放后按天」才要求天数（1-365），与后端 VALID_DAYS_MIN/MAX 同口径
          validator: (rule, value, callback) => {
            if (Number(this.form.valid_type) !== 2) return callback()
            const n = Number(value)
            if (!n || n < 1 || n > 365) {
              return callback(new Error('有效天数范围 1-365'))
            }
            callback()
          },
          trigger: 'change'
        }]
      }
    }
  },
  created() {
    // 只有「里程碑达成条件」这种运营可增删的选项走字典；发放方式是程序固定规则，不查字典
    this.loadDictOptions({ dispatch_milestone: 'milestoneOptions' })
    this.loadData()
  },
  computed: {
    // 已勾选的发放方式标识：决定展开「每月发放」「里程碑发放」哪块参数配置
    selectedDispatchValues() {
      return splitDispatchValues((this.form.dispatchIdList || []).join(','))
    },
    hasMonthly() {
      return this.selectedDispatchValues.indexOf('monthly') > -1
    },
    hasMilestone() {
      return this.selectedDispatchValues.indexOf('milestone') > -1
    },
    // 发放方式全局 radio（映射 milestone_mode：1 固定日期 / 2 周期；列名沿用历史）
    dispatchMode: {
      get() { return Number(this.form.milestone_mode) === 2 ? 2 : 1 },
      set(v) { this.form.milestone_mode = v }
    }
  },
  methods: {
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.category) params.category = this.query.category
      if (this.query.dispatch) params.dispatch = this.query.dispatch
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status
      getBenefitList(params).then(res => {
        if (res.code !== 200) return
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.statusCounts = res.data.status_counts || {}
        this.totalReceived = res.data.total_received || 0
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', category: '', dispatch: '', status: '' }
      this.page = 1
      this.loadData()
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    onCoverChange() {
      this.$nextTick(() => { this.$refs.form && this.$refs.form.validateField('cover') })
    },
    handleCreate() {
      this.form = emptyBenefit()
      this.formVisible = true
      this.$nextTick(() => { this.$refs.form && this.$refs.form.clearValidate() })
    },
    handleEdit(row) {
      const f = JSON.parse(JSON.stringify(row))
      f.dateRange = (f.valid_type === 1 && f.start_date && f.end_date) ? [f.start_date, f.end_date] : []
      // 发放方式库里存的是固定标识串，表单用数组承接勾选（monthly/milestone 可并存）
      f.dispatchIdList = splitDispatchValues(f.dispatch_ids)
      // 未配置达成条件的（旧数据/新建），显示 placeholder 而不是 0；字典 _id 是 ObjectId 串，原样回填不能 Number()（会变 NaN）
      f.milestone_id = f.milestone_id || ''
      // 里程碑发放参数：旧数据无值时给默认（一次性、周期 30 天）；发放张数为券种级共享参数，
      // 老数据若只配过 dispatch_num（每月），回填进共享字段显示，保存时按勾选方式分发回两列
      f.milestone_mode = Number(f.milestone_mode) === 2 ? 2 : 1
      f.milestone_num = Number(f.milestone_num) > 0 ? Number(f.milestone_num) : (Number(f.dispatch_num) > 0 ? Number(f.dispatch_num) : 1)
      f.milestone_interval_days = Number(f.milestone_interval_days) > 0 ? Number(f.milestone_interval_days) : 30
      this.form = f
      this.formVisible = true
      this.$nextTick(() => { this.$refs.form && this.$refs.form.clearValidate() })
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const payload = JSON.parse(JSON.stringify(this.form))
        if (payload.valid_type === 1) {
          const range = payload.dateRange || []
          payload.start_date = range[0] || ''
          payload.end_date = range[1] || ''
          payload.valid_days = 0
        } else if (payload.valid_type === 2) {
          payload.start_date = ''
          payload.end_date = ''
        } else {
          // 3 长期有效：不存日期区间、不存天数（后端发放时 expire_time 存 NULL，永不过期）
          payload.start_date = ''
          payload.end_date = ''
          payload.valid_days = 0
        }
        delete payload.dateRange
        // 发放方式：只提交字典 _id（数组 → _id 串，空 = 仅手动发放）
        // 未勾选的方式不动它的参数值（后端也不覆盖库中原配置），避免编辑时把已有配置抹掉
        payload.dispatch_ids = splitDispatchValues((payload.dispatchIdList || []).join(',')).join(',')
        delete payload.dispatchIdList
        // 自动发放：
        //   单独勾里程碑 = 达成后一次性发（milestone_mode 恒为 1，无周期概念）
        //   同时勾每月+里程碑 = 达成用户二选一：跟随每月发放日 / 每 N 天周期
        if (this.hasMonthly || this.hasMilestone) {
          if (!payload.milestone_num || payload.milestone_num < 1) {
            return this.$message.warning('请填写发放张数')
          }
          payload.dispatch_num = payload.milestone_num
        }
        if (this.hasMonthly) {
          if (!payload.dispatch_day) {
            return this.$message.warning('请选择每月发放日')
          }
        }
        if (this.hasMilestone) {
          if (!payload.milestone_id) {
            return this.$message.warning('请选择达成条件')
          }
          if (this.hasMonthly && this.hasMilestone) {
            if (![1, 2].includes(Number(payload.milestone_mode))) {
              return this.$message.warning('请选择达成用户的发放方式')
            }
            if (Number(payload.milestone_mode) === 2 && (!payload.milestone_interval_days || payload.milestone_interval_days < 1)) {
              return this.$message.warning('请填写周期发放的间隔天数')
            }
          } else {
            // 单独勾里程碑发放：一次性，不存周期参数
            payload.milestone_mode = 1
          }
        }
        this.saving = true
        const call = payload.id ? updateBenefit(payload.id, payload) : createBenefit(payload)
        call.then(res => {
          if (res.code !== 200) return
          this.$message.success(payload.id ? '已保存修改' : '已新增权益券，可在列表点「发放」给用户发券')
          this.formVisible = false
          this.loadData()
        }).finally(() => {
          this.saving = false
        })
      })
    },
    handleGrant(row) {
      this.grantRow = row
      this.grantVisible = true
    },
    handleStatus(row, status) {
      const action = status === 1 ? '启用' : '停用'
      const tip = status === 1
        ? `确认启用「${row.name}」？启用后按发放方式正常发放。`
        : `确认停用「${row.name}」？停用后暂停发放，已发放的券不受影响。`
      this.$confirm(tip, action + '确认', { type: 'warning' })
        .then(() => {
          setBenefitStatus(row.id, { status }).then(res => {
            if (res.code !== 200) return
            this.$message.success('已' + action)
            this.loadData()
          })
        }).catch(() => {})
    },
    handleDelete(row) {
      const extra = row.received_count
        ? `该券已发放 ${row.received_count} 张，删除后用户手上未使用的券将无法核销，请谨慎操作。`
        : '删除后不可恢复。'
      this.$confirm(`确认删除「${row.name}」？${extra}`, '删除确认', {
        type: 'warning',
        confirmButtonText: '确认删除',
        confirmButtonClass: 'el-button--danger'
      }).then(() => {
        deleteBenefit(row.id).then(res => {
          if (res.code !== 200) return
          this.$message.success('已删除')
          // 当前页删空时回退一页
          if (this.list.length === 1 && this.page > 1) this.page -= 1
          this.loadData()
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
/* 券单元格 */
.benefit-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.benefit-cover {
  flex: 0 0 68px;
  width: 68px;
  height: 46px;
  border-radius: 6px;
  overflow: hidden;
  background: #faf7f1;
  border: 1px solid #f0ebe2;
}
.benefit-main {
  min-width: 0;
}
.benefit-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  line-height: 1.4;
}
.benefit-desc {
  margin-top: 3px;
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cell-muted {
  color: #c0c4cc;
}
/* 发放方式 */
.dispatch-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
.dispatch-next {
  font-size: 12px;
  color: #b88230;
  line-height: 1.4;
}
.text-primary-btn {
  color: var(--primary);
  font-weight: 600;
}
/* 发放量 */
.stock-cell {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding-right: 12px;
}
.stock-text {
  font-size: 12.5px;
  color: #606266;
}
/* 表单 */
.benefit-form-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}
.form-section + .form-section {
  margin-top: 6px;
}
.form-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  padding-left: 9px;
  border-left: 3px solid var(--primary);
  margin-bottom: 14px;
}
.form-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #a8abb2;
}
/* 周期发放/发放张数的单位字 */
.cycle-unit {
  margin-left: 6px;
  font-size: 13px;
  color: #606266;
}
</style>
